'use client';

/**
 * GlossyMenu Component
 *
 * A dropdown menu with glassmorphism effect and smooth animations.
 * GlossyMenuItem includes built-in security props for permission/tier/license locking.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import LockIcon from '@mui/icons-material/Lock';
import Divider from '@mui/material/Divider';
import Grow from '@mui/material/Grow';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu, { type MenuProps } from '@mui/material/Menu';
import MenuItem, { type MenuItemProps } from '@mui/material/MenuItem';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { SecurityProps } from '@ops-matrix/shared-types';
import React, { forwardRef, useCallback } from 'react';
import { useSecurityLock } from '../../hooks/useSecurityLock';
import { getTierLabel } from '../../providers/PermissionProvider';
import { baseColors } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import { GlassChip } from '../GlassChip/GlassChip';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Types
// ============================================================================

export interface GlossyMenuProps extends Omit<MenuProps, 'TransitionComponent'> {
  /**
   * Enable glassmorphism effect
   * @default true
   */
  glass?: boolean;

  /**
   * Animation type
   * @default 'grow'
   */
  animation?: 'grow' | 'fade' | 'none';
}

export interface GlossyMenuItemProps extends MenuItemProps, SecurityProps {
  /**
   * Icon to display
   */
  icon?: React.ReactNode;

  /**
   * Secondary text
   */
  secondaryText?: string;

  /**
   * Keyboard shortcut hint
   */
  shortcut?: string;

  /**
   * Danger/destructive item styling
   * @default false
   */
  danger?: boolean;

  /**
   * Item is active/selected
   * @default false
   */
  active?: boolean;

  /**
   * Label text (alternative to children)
   */
  label?: string;
}

export interface GlossyMenuSectionProps {
  /**
   * Section title
   */
  title?: string;

  /**
   * Section items
   */
  children: React.ReactNode;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledMenuProps {
  $isDark: boolean;
  $glass: boolean;
}

const StyledMenu = styled(Menu, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledMenuProps>(({ theme, $isDark, $glass }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    '& .MuiPaper-root': {
      borderRadius: 16,
      minWidth: 200,
      marginTop: 8,
      overflow: 'hidden',
      ...($glass && {
        background: $isDark ? 'rgba(28, 28, 30, 0.72)' : 'rgba(255, 255, 255, 0.78)',
        backdropFilter: glass.backdropBlur,
        WebkitBackdropFilter: glass.backdropBlur,
        border: `0.5px solid ${glass.border}`,
        boxShadow: `${glass.shadow}, inset 0 0.5px 0 ${$isDark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.5)'}`,
      }),
    },

    '& .MuiMenu-list': {
      padding: theme.spacing(1),
    },
  };
});

interface StyledMenuItemProps {
  $isDark: boolean;
  $danger: boolean;
  $active: boolean;
  $isLocked?: boolean;
}

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledMenuItemProps>(({ theme, $isDark, $danger, $active, $isLocked }) => ({
  borderRadius: 12,
  padding: '8px 16px',
  margin: theme.spacing(0.25, 0),
  transition: transitionPresets.allFast,
  minHeight: 44, // Apple standard touch target
  opacity: $isLocked ? 0.7 : 1,

  // Danger styling
  ...($danger && {
    color: theme.palette.error.main,
    '& .MuiListItemIcon-root': {
      color: theme.palette.error.main,
    },
  }),

  // Active/selected styling
  ...($active && {
    backgroundColor: alpha(baseColors.primary[500], $isDark ? 0.2 : 0.1),
    '&:hover': {
      backgroundColor: alpha(baseColors.primary[500], $isDark ? 0.25 : 0.15),
    },
  }),

  // Hover styling (disabled for locked items)
  '&:hover': {
    backgroundColor: $isLocked ? 'transparent' : $isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
    cursor: $isLocked ? 'default' : 'pointer',
  },

  // Icon styling
  '& .MuiListItemIcon-root': {
    minWidth: 32,
    color: $isDark ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
  },

  // Text styling
  '& .MuiListItemText-primary': {
    fontSize: '0.9375rem', // 15pt
    fontWeight: 500,
  },

  '& .MuiListItemText-secondary': {
    fontSize: '0.8125rem', // 13pt
    opacity: 0.7,
  },
}));

const ShortcutHint = styled('span')(({ theme }) => ({
  marginLeft: 'auto',
  paddingLeft: theme.spacing(3),
  fontSize: '0.75rem',
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
  fontFamily: 'monospace',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  padding: '8px 16px 4px 16px',
  fontSize: '0.8125rem', // 13pt Apple caption
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(0.75, 0),
  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
}));

// ============================================================================
// Components
// ============================================================================

/**
 * GlossyMenu - Main menu container
 */
export const GlossyMenu = forwardRef<HTMLDivElement, GlossyMenuProps>(
  ({ children, glass = true, animation = 'grow', ...props }, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const TransitionComponent = animation === 'grow' ? Grow : undefined;

    return (
      <StyledMenu ref={ref} $isDark={isDark} $glass={glass} slots={{ transition: TransitionComponent }} {...props}>
        {children}
      </StyledMenu>
    );
  },
);

GlossyMenu.displayName = 'GlossyMenu';

/**
 * GlossyMenuItem - Individual menu item with built-in security
 */
export const GlossyMenuItem = forwardRef<HTMLLIElement, GlossyMenuItemProps>(
  (
    {
      children,
      icon,
      secondaryText,
      shortcut,
      danger = false,
      active = false,
      disabled,
      onClick,
      label,
      // Security props
      permission,
      anyPermission,
      allPermissions,
      requiredTier,
      requiredLicense,
      anyLicense,
      showLockIcon = true,
      showTierBadge = true,
      onLockedClick,
      lockedTooltip,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Security lock state
    const { lockState, isLocked, handleLockedClick, lockedTooltipText } = useSecurityLock({
      permission,
      anyPermission,
      allPermissions,
      requiredTier,
      requiredLicense,
      anyLicense,
      onLockedClick,
      lockedTooltip,
    });

    const isDisabled = disabled || isLocked;

    // Handle click with security interception
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLLIElement>) => {
        if (isLocked) {
          e.preventDefault();
          e.stopPropagation();
          handleLockedClick();
          return;
        }
        onClick?.(e);
      },
      [isLocked, handleLockedClick, onClick],
    );

    const menuItemContent = (
      <StyledMenuItem
        ref={ref}
        $isDark={isDark}
        $danger={danger}
        $active={active}
        $isLocked={isLocked}
        disabled={isDisabled}
        onClick={handleClick}
        {...props}
      >
        {/* Icon */}
        {icon && <ListItemIcon sx={{ color: isDisabled ? 'action.disabled' : 'inherit' }}>{icon}</ListItemIcon>}

        {/* Label and secondary text */}
        <ListItemText
          primary={label ?? children}
          secondary={secondaryText}
          sx={{
            color: isDisabled ? 'text.disabled' : 'inherit',
          }}
        />

        {/* Shortcut hint */}
        {shortcut && !isLocked && <ShortcutHint>{shortcut}</ShortcutHint>}

        {/* Lock indicator and tier badge */}
        {isLocked && showLockIcon && lockState && (
          <>
            <LockIcon
              sx={{
                fontSize: iconSizes.sm,
                color:
                  lockState.type === 'tier'
                    ? theme.palette.warning.main
                    : lockState.type === 'license'
                      ? theme.palette.info.main
                      : theme.palette.action.disabled,
                ml: 1,
              }}
            />
            {showTierBadge && lockState.type === 'tier' && lockState.requiredTier && (
              <GlassChip
                label={getTierLabel(lockState.requiredTier)}
                size="small"
                color="warning"
                sx={{
                  ml: 0.5,
                  height: 18,
                  fontSize: '0.75rem',
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            )}
          </>
        )}
      </StyledMenuItem>
    );

    // Wrap with tooltip if locked
    if (isLocked) {
      return (
        <GlassTooltip title={lockedTooltipText} placement="left">
          {menuItemContent}
        </GlassTooltip>
      );
    }

    return menuItemContent;
  },
);

GlossyMenuItem.displayName = 'GlossyMenuItem';

/**
 * GlossyMenuSection - Group of menu items with optional title
 * Note: Returns an array instead of Fragment to avoid MUI Menu warning
 */
export const GlossyMenuSection: React.FC<GlossyMenuSectionProps> = ({ title, children }) => {
  // Return array of elements instead of Fragment for MUI Menu compatibility
  const elements: React.ReactNode[] = [];
  if (title) {
    elements.push(<SectionTitle key="section-title">{title}</SectionTitle>);
  }
  // Flatten children into the array
  React.Children.forEach(children, (child, index) => {
    if (child) {
      elements.push(
        React.isValidElement(child) ? React.cloneElement(child, { key: child.key ?? `item-${index}` }) : child,
      );
    }
  });
  return elements as unknown as React.ReactElement;
};

/**
 * GlossyMenuDivider - Separator between menu sections
 */
export const GlossyMenuDivider: React.FC = () => {
  return <StyledDivider />;
};

export default GlossyMenu;
